import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEOHead from "../components/SEO/SEOHead";
import Breadcrumbs from "../components/SEO/Breadcrumbs";
import { getWebSiteSchema, getOrganizationSchema, getTravelAgencySchema } from "../components/SEO/schemaTemplates";
import { getTourListings, getTourImages } from "../services/guideService";
import CurrencyService from "../services/currencyService";
import { getBackendBaseUrl } from "../utils/apiConfig";
import { FaStar, FaMapMarkerAlt, FaSearch, FaUserCheck, FaCompass, FaChevronRight, FaUserTie } from "react-icons/fa";
import "./HotelsHubPage.css";

const GUIDE_CATEGORIES = [
  { slug: "all", title: "All Tours & Guides" },
  { slug: "wildlife-guides", title: "Wildlife & Safari", keywords: ["safari", "wildlife", "yala", "whale", "leopard", "elephant", "bird"] },
  { slug: "heritage-guides", title: "Cultural & Heritage", keywords: ["heritage", "culture", "cultural", "temple", "kandy", "sigiriya", "galle", "dance", "history"] },
  { slug: "chauffeur-guides", title: "Chauffeur & Drivers", keywords: ["chauffeur", "driver", "car", "transfer", "transport", "drive", "private"] },
  { slug: "trekking-guides", title: "Hiking & Trekking", keywords: ["trek", "hike", "hiking", "knuckles", "forest", "mountain", "peak", "ella"] },
  { slug: "surf-guides", title: "Surfing & Coastal", keywords: ["surf", "surfing", "beach", "weligama", "mirissa", "arugam", "coastal", "boat"] }
];

export default function GuidesHubPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeCategory = searchParams.get("category") || "all";
  const locationParam = searchParams.get("location") || "";
  const initialSearch = searchParams.get("search") || locationParam || "";

  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [, setCurrency] = useState(CurrencyService.selectedCurrency);
  const BACKEND_URL = getBackendBaseUrl();

  useEffect(() => {
    const onCurrencyChange = (e) => setCurrency(e.detail);
    window.addEventListener("currencyUpdate", onCurrencyChange);
    return () => window.removeEventListener("currencyUpdate", onCurrencyChange);
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    getTourListings()
      .then(async (toursData) => {
        if (!isMounted) return;
        const list = Array.isArray(toursData) ? toursData : [];
        
        // Fetch images for tours in parallel
        const toursWithImages = await Promise.all(
          list.map(async (tour) => {
            try {
              const data = await getTourImages(tour.id);
              const images = Array.isArray(data)
                ? data
                : Array.isArray(data?.images)
                ? data.images
                : [];
              const primaryImage = images.find((img) => img.is_primary) || images[0];
              return {
                ...tour,
                mainImage: primaryImage
                  ? primaryImage.url?.startsWith("http")
                    ? primaryImage.url
                    : `${BACKEND_URL}${primaryImage.url}`
                  : null,
              };
            } catch (err) {
              return { ...tour, mainImage: null };
            }
          })
        );

        if (isMounted) {
          setTours(toursWithImages);
        }
      })
      .catch((err) => {
        console.error("Error fetching tour listings:", err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [BACKEND_URL]);

  // Dynamic counts per category
  const categoryCounts = useMemo(() => {
    const counts = { all: tours.length };
    GUIDE_CATEGORIES.forEach((cat) => {
      if (cat.slug !== "all") {
        counts[cat.slug] = tours.filter((t) => {
          const text = `${t.title || ""} ${t.description || ""} ${t.service_area || ""}`.toLowerCase();
          return cat.keywords.some((kw) => text.includes(kw));
        }).length;
      }
    });
    return counts;
  }, [tours]);

  // Filtered tours
  const filteredTours = useMemo(() => {
    return tours.filter((tour) => {
      // 1. Category Filter
      if (activeCategory !== "all") {
        const cat = GUIDE_CATEGORIES.find((c) => c.slug === activeCategory);
        if (cat && cat.keywords) {
          const text = `${tour.title || ""} ${tour.description || ""} ${tour.service_area || ""}`.toLowerCase();
          const matches = cat.keywords.some((kw) => text.includes(kw));
          if (!matches) return false;
        }
      }

      // 2. Search Query
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const text = `${tour.title || ""} ${tour.guide_name || ""} ${tour.description || ""} ${tour.service_area || ""}`.toLowerCase();
        if (!text.includes(query)) return false;
      }

      return true;
    });
  }, [tours, activeCategory, searchTerm]);

  const handleCategorySelect = (slug) => {
    if (slug === "all") {
      navigate("/guides");
    } else {
      navigate(`/guides?category=${slug}`);
    }
  };

  const handleTourClick = (tour) => {
    navigate(`/guideprofilebook/${tour.id}`, { state: { guide: tour } });
  };

  const currentCategoryTitle =
    GUIDE_CATEGORIES.find((c) => c.slug === activeCategory)?.title || "All Guides & Tours";

  const breadcrumbItems = [
    { name: "Sri Lanka Guides", url: "/guides" },
    ...(activeCategory !== "all" ? [{ name: currentCategoryTitle, url: `/guides?category=${activeCategory}` }] : [])
  ];

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [getWebSiteSchema(), getOrganizationSchema(), getTravelAgencySchema()]
  };

  return (
    <div className="seo-page-wrapper">
      <SEOHead
        title={
          activeCategory !== "all"
            ? `${currentCategoryTitle} in Sri Lanka | Soloway Verified Guides`
            : "Certified Sri Lanka Guides & Chauffeur Drivers | Soloway"
        }
        description="Hire certified, government-licensed Sri Lanka tour guides and private chauffeurs. Multilingual guides fluent in English, German, French, and Russian. Book securely on Soloway."
        keywords="sri lanka guides, sri lanka tour driver, hire guide sri lanka, private chauffeur sri lanka, certified tour guide colombo, ella hiking guide, wildlife tracker yala"
        canonicalUrl="/sri-lanka-guides"
        structuredData={combinedSchema}
      />
      <Navbar />

      <main className="seo-page-container">
        <div className="content-inner">
          <Breadcrumbs items={breadcrumbItems} />

          {/* Page Header */}
          <header className="page-header-seo">
            <span className="seo-badge">SLTDA Certified Local Tourism Experts</span>
            <h1>
              {activeCategory !== "all"
                ? `${currentCategoryTitle} in Sri Lanka`
                : "Hire Certified Sri Lanka Guides & Private Chauffeurs"}
            </h1>
            <p className="lead-text">
              Connect directly with verified Sri Lankan tour guides, wildlife trackers, and private drivers to explore the island with safety, transparent pricing, and authentic local knowledge.
            </p>
          </header>

          {/* Search & Category Filter Section */}
          <section className="hub-search-filter-section">
            <div className="hub-search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search tour activity, guide name, or destination (e.g. Yala, Galle, Knuckles)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="clear-search-btn" onClick={() => setSearchTerm("")}>
                  ×
                </button>
              )}
            </div>

            {/* Category Filter Chips */}
            <div className="city-filter-scroll">
              {GUIDE_CATEGORIES.map((cat) => {
                const count = categoryCounts[cat.slug] ?? 0;
                const isSelected = activeCategory === cat.slug;
                return (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategorySelect(cat.slug)}
                    className={`city-filter-chip ${isSelected ? "active" : ""}`}
                  >
                    <span>{cat.title}</span>
                    <span className="chip-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Live Tours & Guides Grid */}
          <section className="live-listings-section" aria-labelledby="guides-grid-heading">
            <div className="section-title-row">
              <div>
                <h2 id="guides-grid-heading">
                  {activeCategory !== "all" ? currentCategoryTitle : "Available Guided Experiences"}
                </h2>
                <span className="live-results-count">
                  Showing {filteredTours.length} {filteredTours.length === 1 ? "tour & guide" : "tours & guides"}
                </span>
              </div>
              <span className="total-stays-count">
                <FaUserCheck style={{ marginRight: 4 }} /> 100% Verified Local Guides
              </span>
            </div>

            {loading ? (
              <div className="hub-loading-state">
                <div className="hub-spinner"></div>
                <p>Loading real tour listings and verified guides from database...</p>
              </div>
            ) : filteredTours.length === 0 ? (
              <div className="hub-empty-state">
                <FaCompass className="empty-icon" />
                <h3>No guides or tours found matching your search</h3>
                <p>Try selecting "All Tours & Guides" or clearing your search term to see all available experiences.</p>
                <button
                  className="reset-filter-btn"
                  onClick={() => {
                    handleCategorySelect("all");
                    setSearchTerm("");
                  }}
                >
                  View All Guides
                </button>
              </div>
            ) : (
              <div className="hotel-listings-grid">
                {filteredTours.map((tour) => {
                  const rating = parseFloat(tour.average_rating) || 5.0;
                  const reviews = tour.review_count || 0;
                  const price = parseFloat(tour.price) || 0;
                  const convertedPrice = CurrencyService.convert(price, { fromCurrency: "LKR" });
                  const currencySymbol = CurrencyService.getCurrencySymbol(CurrencyService.selectedCurrency);
                  
                  // Extract clean guide display name if title has "with [Name]"
                  const titleMatch = tour.title?.match(/with\s+([A-Za-z]+)/i);
                  const displayGuide =
                    tour.guide_name && tour.guide_name !== "Unknown Guide"
                      ? tour.guide_name
                      : titleMatch
                      ? `${titleMatch[1]} Silva`
                      : "Verified Local Guide";

                  return (
                    <article
                      key={tour.id}
                      className="hotel-card-premium"
                      onClick={() => handleTourClick(tour)}
                    >
                      <div className="hotel-image-wrapper">
                        <img
                          src={
                            tour.mainImage ||
                            "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&auto=format&fit=crop&q=80"
                          }
                          alt={tour.title || "Sri Lanka Tour"}
                          className="hotel-card-img"
                          loading="lazy"
                        />
                        <span className="verified-badge">
                          <FaUserCheck /> Licensed Guide
                        </span>
                        {price > 0 && (
                          <div className="hotel-price-badge">
                            <span className="price-num">
                              {currencySymbol} {convertedPrice.toFixed(0)}
                            </span>
                            <span className="price-unit">/ person</span>
                          </div>
                        )}
                      </div>

                      <div className="hotel-card-body">
                        <div className="hotel-rating-row">
                          <div className="rating-pill">
                            <FaStar className="star-icon" />
                            <span>{rating > 0 ? rating.toFixed(1) : "5.0"}</span>
                          </div>
                          <span className="review-count-text">
                            {reviews > 0 ? `(${reviews} reviews)` : "(Top Rated)"}
                          </span>
                        </div>

                        <h3 className="hotel-card-title">{tour.title}</h3>

                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "#0d9488", fontSize: "0.85rem", fontWeight: 600 }}>
                          <FaUserTie style={{ fontSize: "0.8rem" }} />
                          <span>Guide: {displayGuide}</span>
                        </div>

                        <p className="hotel-location-text">
                          <FaMapMarkerAlt className="location-icon" />
                          <span>{tour.service_area || "Sri Lanka Nationwide"}</span>
                        </p>

                        <div className="hotel-card-footer">
                          <span className="availability-tag">Free Cancellation</span>
                          <button className="view-hotel-btn">
                            Book Guide <FaChevronRight style={{ fontSize: "0.75rem", marginLeft: 4 }} />
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {/* Guide Specialized Categories Description */}
          <section className="destinations-grid-section" aria-labelledby="guides-info-heading">
            <div className="section-title-row">
              <h2 id="guides-info-heading">Specialized Guide Categories</h2>
              <span className="total-stays-count">SLTDA Certified</span>
            </div>

            <div className="destinations-grid">
              {GUIDE_CATEGORIES
                .filter((c) => c.slug !== "all")
                .map((cat) => (
                  <article key={cat.slug} className="city-card">
                    <div className="city-card-header">
                      <span className="city-price-tag">
                        {categoryCounts[cat.slug] || 0} Listed Tours
                      </span>
                    </div>
                    <h3>
                      <Link to={`/guides?category=${cat.slug}`} className="city-card-title-link">
                        {cat.title}
                      </Link>
                    </h3>
                    <p>
                      Connect with experienced, government-licensed guides specializing in {cat.title.toLowerCase()} across Sri Lanka.
                    </p>
                    <div className="city-card-footer">
                      <span className="listing-count">Identity Verified</span>
                      <Link to={`/guides?category=${cat.slug}`} className="view-link">
                        View Guides &rarr;
                      </Link>
                    </div>
                  </article>
                ))}
            </div>
          </section>

          {/* SEO Rich Info Section */}
          <article className="seo-rich-content">
            <h2>Why Book Licensed Sri Lanka Guides on Soloway?</h2>
            <p>
              Navigating Sri Lanka with a certified guide unlocks authentic stories, hidden waterfalls, and stress-free transport. On Soloway:
            </p>
            <ul>
              <li><strong>Government Verified:</strong> Every guide holds a valid Sri Lanka Tourism Development Authority (SLTDA) license or certified national badge.</li>
              <li><strong>Direct Pricing:</strong> No middleman agency commissions. Pay local rates directly with secure Payments.lk checkout.</li>
              <li><strong>Safety & Support:</strong> 24/7 SOS safety support and comprehensive traveler assistance across every province.</li>
            </ul>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
